// Community Browse Subpage - Browse community apps
// Full-width layout (like Discord hub): search bar at top, app grid below
// URL-synced filters (query, sort, tags, includeAll) - shareable, refreshable
// Follows marketplace/downloads pattern

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useManager } from '../../../../core';
import { useAuth, useAuthRequired } from '../../../../contexts/AuthContext';
import { eventUtils } from '../../../../core/utils';
import { CommunityManager } from '../../managers/community-manager';
import { BrowseSearchBar, CommunityAppGrid } from './components';
import '../../CommunityShared.css';
import '../../CommunitySection.css';

const SEARCH_DEBOUNCE_MS = 300;

export function CommunityBrowseSection() {
  const auth = useAuthRequired({
    payload: {
      action: 'viewCommunityBrowse',
      section: 'community',
      timestamp: Date.now(),
    },
    requireAuth: true,
    requireSupporter: false,
  });
  const { user } = useAuth();
  const location = useLocation();
  const isAdminPath = location.pathname.includes('/community/admin/');
  const isAdminView = isAdminPath;
  const isAdmin = isAdminPath && user?.role === 'admin';

  const [searchParams, setSearchParams] = useSearchParams();
  const { manager, isInitialized } = useManager(CommunityManager);

  // URL as source of truth (shareable, refreshable)
  const urlQuery = searchParams.get('query') || '';
  const urlSearchBy = searchParams.get('searchBy') || 'name';
  const urlSort = searchParams.get('sort') || 'trending';
  const tagsParam = searchParams.get('tags') || '';
  const urlTags = useMemo(() => tagsParam.split(',').filter(Boolean), [tagsParam]);
  const urlVisibility = isAdmin ? (searchParams.get('visibility') || 'public') : 'public';
  const urlSyncStatus = isAdmin ? (searchParams.get('syncStatus') || 'all') : 'all';
  const urlTeamStatus = isAdmin ? (searchParams.get('teamStatus') || 'all') : 'all';

  // Local state for responsive input (synced from URL)
  const [localSearchTerm, setLocalSearchTerm] = useState(urlQuery);

  const [apps, setApps] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);

  // Sync local search when URL changes (shared link, back button, or our debounced update)
  useEffect(() => {
    setLocalSearchTerm(urlQuery);
  }, [urlQuery]);

  // Debounced URL update for search (300ms)
  const updateUrlFromQuery = useCallback(
    (query) => {
      const newParams = new URLSearchParams(searchParams);
      if (query && query.trim()) {
        newParams.set('query', query.trim());
      } else {
        newParams.delete('query');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const debouncedUrlUpdateRef = useRef(null);
  useEffect(() => {
    debouncedUrlUpdateRef.current = eventUtils.debounce(updateUrlFromQuery, SEARCH_DEBOUNCE_MS);
  }, [updateUrlFromQuery]);
  const debouncedUrlUpdate = useCallback((...args) => {
    debouncedUrlUpdateRef.current?.(...args);
  }, []);

  const handleSearchChange = useCallback(
    (value) => {
      setLocalSearchTerm(value);
      debouncedUrlUpdate(value);
    },
    [debouncedUrlUpdate]
  );

  const handleSearchByChange = useCallback(
    (value) => {
      const newParams = new URLSearchParams(searchParams);
      const next = value === 'author' ? 'author' : 'name';
      if (next === 'name') newParams.delete('searchBy');
      else newParams.set('searchBy', next);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSortChange = useCallback(
    (value) => {
      const newParams = new URLSearchParams(searchParams);
      if (value && value !== 'trending') {
        newParams.set('sort', value);
      } else {
        newParams.delete('sort');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleTagsChange = useCallback(
    (newTags) => {
      const newParams = new URLSearchParams(searchParams);
      if (newTags?.length > 0) {
        newParams.set('tags', newTags.join(','));
      } else {
        newParams.delete('tags');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleVisibilityChange = useCallback(
    (value) => {
      const newParams = new URLSearchParams(searchParams);
      if (value && value !== 'public') {
        newParams.set('visibility', value);
      } else {
        newParams.delete('visibility');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSyncStatusChange = useCallback(
    (value) => {
      const newParams = new URLSearchParams(searchParams);
      if (value && value !== 'all') {
        newParams.set('syncStatus', value);
      } else {
        newParams.delete('syncStatus');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleTeamStatusChange = useCallback(
    (value) => {
      const newParams = new URLSearchParams(searchParams);
      if (value && value !== 'all') {
        newParams.set('teamStatus', value);
      } else {
        newParams.delete('teamStatus');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const fetchPage = useCallback(
    async (cursor = null, append = false) => {
      if (!manager) return;
      const isFirst = !append;
      if (isFirst) setLoading(true);
      else setLoadingMore(true);
      try {
        const { apps: pageApps, nextCursor: nc, hasMore: hm } = await manager.fetchBrowseApps({
          q: urlQuery,
          searchBy: urlSearchBy,
          tags: urlTags,
          sort: urlSort,
          cursor,
          limit: 20,
          visibility: urlVisibility,
          syncStatus: urlSyncStatus,
          teamStatus: urlTeamStatus,
          adminView: isAdminView,
        });
        if (append) {
          setApps((prev) => [...prev, ...pageApps]);
        } else {
          setApps(pageApps);
        }
        setNextCursor(nc);
        setHasMore(hm);
      } catch (err) {
        console.error('Failed to fetch community apps:', err);
        if (!append) setApps([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [manager, urlQuery, urlSearchBy, urlTags, urlSort, urlVisibility, urlSyncStatus, urlTeamStatus, isAdminView]
  );

  // Initial fetch and refetch when URL filters change (layout ensures we're a supporter)
  useEffect(() => {
    if (isInitialized && manager) {
      fetchPage();
    }
  }, [isInitialized, manager, urlQuery, urlSearchBy, urlTags, urlSort, urlVisibility, urlSyncStatus, urlTeamStatus, fetchPage]);

  // Infinite scroll: load more when sentinel is visible
  useEffect(() => {
    if (!hasMore || loadingMore || !loadMoreRef.current) return;
    const el = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && nextCursor && !loadingMore) {
          fetchPage(nextCursor, true);
        }
      },
      { rootMargin: '100px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, nextCursor, loadingMore, fetchPage]);

  if (auth.loading || !auth.isAuthorized) return null;

  if (!isInitialized) {
    return (
      <div className="community-content-centered">
        <div className="community-panel">
          <div style={{ color: '#999' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="browse-hub">
          <section className="browse-hub-search">
            <BrowseSearchBar
              searchQuery={localSearchTerm}
              searchBy={urlSearchBy}
              onSearchByChange={handleSearchByChange}
              onSearchChange={handleSearchChange}
              sortBy={urlSort}
              onSortChange={handleSortChange}
              tags={urlTags}
              onTagsChange={handleTagsChange}
              visibility={urlVisibility}
              onVisibilityChange={handleVisibilityChange}
              syncStatus={urlSyncStatus}
              onSyncStatusChange={handleSyncStatusChange}
              teamStatus={urlTeamStatus}
              onTeamStatusChange={handleTeamStatusChange}
              isAdmin={isAdmin}
            />
          </section>
          <section className="browse-hub-content">
            <CommunityAppGrid
              apps={apps}
              loading={loading}
              emptyMessage="No community apps found. Try adjusting your search or filters."
              loadMoreRef={loadMoreRef}
              isAdmin={isAdmin}
              manager={manager}
              onRefreshApps={() => fetchPage()}
            />
            {loadingMore && apps.length > 0 && (
              <div className="community-app-grid-load-more">
                <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Loading more...
              </div>
            )}
          </section>
        </div>
  );
}
